import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { usePermissions } from '../../../../front/src/hooks/usePermissions';
import { useWebsite } from '../../../../front/src/hooks/useWebsite';
import { isAndroidBrowser, isIOSSafariBrowser } from '../../../../front/src/utils/browser-utils';
import { NoSSR } from '../../../../front/src/utils/ssr';
import { debateConfig } from '../../../config';
import { useIsAlphaCircleOrTestDebate } from '../../utils/common';
import { AddToHomeScreenAndroid } from './AddToHomeScreenAndroid';
import { AddToHomeScreenIOS } from './AddToHomeScreenIOS';

interface Props {}

export const InitPushNotification: FC<Props> = memo(function InitPushNotification({}) {
  const [isOneSignalInit, setIsOneSignalInit] = useState(false);
  const [isOneSignalErrored, setIsOneSignalErrored] = useState(false);

  const { authUserId, email, username, isAuthDone } = usePermissions();

  const website = useWebsite();
  const oneSignalConfig = website.pushWeb?.oneSignal; // can be null

  const isAC = useIsAlphaCircleOrTestDebate();

  const shouldHandleNotifs = useMemo(
    () =>
      isAC &&
      isAuthDone &&
      (!debateConfig.isDev || debateConfig.enableNotifInDev) &&
      oneSignalConfig?.active &&
      !!oneSignalConfig?.appId,
    [isAC, isAuthDone, oneSignalConfig],
  );

  const initOneSignalNotifications = useCallback(async () => {
    try {
      const { initOneSignal } = await import('./InitPushNotificationDynamic');
      const initState = !!(await initOneSignal(oneSignalConfig));
      setIsOneSignalInit(initState);
    } catch (error: any) {
      console.error('🔔 [OneSignal] init error:', error);
      setIsOneSignalErrored(!!error);
    }
  }, [oneSignalConfig]);

  const handleOneSignalBellHideOnIosIfAlreadySubscribed = useCallback(() => {
    const isIosPWA = isIOSSafariBrowser();
    const osUID = localStorage.getItem('os_uid');

    if (isIosPWA && osUID) {
      const bellElement = document.getElementById('onesignal-bell-launcher');
      if (bellElement) {
        bellElement.style.display = 'none';
      }
    }
  }, []);

  useEffect(() => {
    if (shouldHandleNotifs && !isOneSignalInit) {
      initOneSignalNotifications();
    }
  }, [shouldHandleNotifs, isOneSignalInit, initOneSignalNotifications]);

  useEffect(() => {
    if (isOneSignalInit) {
      (async () => {
        try {
          const { updateOneSignalLoginLogout } = await import('./InitPushNotificationDynamic');
          updateOneSignalLoginLogout(authUserId, email, username);
        } catch (error: any) {
          console.error('🔔 [OneSignal] updateOneSignalLoginLogout error:', error);
          setIsOneSignalErrored(!!error);
        }
      })();
    }
  }, [authUserId, email, isOneSignalInit, username]);

  // Hide OneSignal bell on iOS if already subscribed
  // This is a hack to prevent the bell from showing on iOS if the user is already subscribed (known issue by OneSignal)
  useEffect(() => {
    if (isOneSignalInit) {
      handleOneSignalBellHideOnIosIfAlreadySubscribed();
    }
  }, [isOneSignalInit, handleOneSignalBellHideOnIosIfAlreadySubscribed]);

  if (!isAC || !authUserId || !debateConfig.enablePWAInstallPrompt || isOneSignalErrored)
    return null;

  const inSafariBrowser = isIOSSafariBrowser();
  const inAndroidBrowser = debateConfig.enableAndroidPWAInstallPrompt && isAndroidBrowser();

  return (
    <NoSSR>
      {shouldHandleNotifs &&
        (inSafariBrowser ? (
          <AddToHomeScreenIOS />
        ) : inAndroidBrowser ? (
          <AddToHomeScreenAndroid />
        ) : null)}
    </NoSSR>
  );
});

export default InitPushNotification;
